import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useCheckout } from 'lib/modules/checkout'
import { useCustomer } from 'lib/core/hooks'
import { DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import {
  setGooglePayPaymentMethodOnCartMutation,
  SetVaultPaymentMethodOnCartResponse
} from '../utils'
import { getCommerceHubGooglePayConfig } from '../config'

declare global {
  interface Window {
    google?: any
  }
}

interface PlaceGooglePayOrderParams {
  onError: (e: Error) => void
}

// SDK-load: same lazy <Helmet><script> + polling pattern as the Affirm module
// (lib/modules/affirm/components/AffirmMain/index.tsx:141-176) - only loaded
// while this component is mounted, not the global bundle.
const scriptIsLoaded = () => Boolean(window.google?.payments?.api?.PaymentsClient)

export const useGooglePayLoaded = () => {
  const [loaded, setLoaded] = useState(scriptIsLoaded())

  useEffect(() => {
    if (loaded) {
      return
    }

    const id = window.setInterval(() => {
      if (scriptIsLoaded()) {
        setLoaded(true)
      }
    }, 500)

    return () => {
      window.clearInterval(id)
    }
  }, [loaded])

  return { loaded }
}

const buildAllowedPaymentMethods = (config: ReturnType<typeof getCommerceHubGooglePayConfig>) => ([{
  type: 'CARD',
  parameters: {
    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
    allowedCardNetworks: config.networks
  },
  tokenizationSpecification: {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway: config.gateway,
      gatewayMerchantId: config.gatewayMerchantId
    }
  }
}])

export const useCommerceHubGooglePay = () => {
  const { cartId, orderTotal, lockCheckout, unlockCheckout, placeOrder } = useCheckout()
  const { isLoggedIn } = useCustomer()
  const { loaded } = useGooglePayLoaded()
  const [available, setAvailable] = useState(false)
  const [eligibilityLoading, setEligibilityLoading] = useState(true)
  // A credential can only be stored against a customer account - the backend
  // ignores the flag for guests, so the opt-in is not offered to them either.
  const [saveCard, setSaveCard] = useState(false)
  const config = getCommerceHubGooglePayConfig()

  // Eligibility is async (unlike Apple's synchronous canMakePayments) - loading-state
  // until it resolves, per architecture §2.1.
  useEffect(() => {
    if (!loaded || !window.google?.payments?.api?.PaymentsClient) {
      return
    }

    let cancelled = false
    setEligibilityLoading(true)

    const client = new window.google.payments.api.PaymentsClient({ environment: config.environment })

    client.isReadyToPay({
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: buildAllowedPaymentMethods(config)
    }).then((response: { result: boolean }) => {
      if (!cancelled) {
        setAvailable(Boolean(response?.result))
      }
    }).catch(() => {
      if (!cancelled) {
        setAvailable(false)
      }
    }).finally(() => {
      if (!cancelled) {
        setEligibilityLoading(false)
      }
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line
  }, [loaded])

  const [setGooglePayOnCart] = useMutation<{
    setPaymentMethodOnCart: SetVaultPaymentMethodOnCartResponse
  }>(setGooglePayPaymentMethodOnCartMutation)

  /**
   * NOTE (architecture §7 Q6): wired to the component's own button onClick (not
   * the generic `usePaymentMethod` "Place Order" flow) so the wallet-sheet gesture
   * stays user-initiated, same reasoning as Apple Pay.
   */
  const placeGooglePayOrder = async (params: PlaceGooglePayOrderParams) => {
    if (!window.google?.payments?.api?.PaymentsClient) {
      params.onError(new Error('Google Pay is not available in this browser'))
      return
    }

    const client = new window.google.payments.api.PaymentsClient({ environment: config.environment })

    try {
      const response = await client.loadPaymentData({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: buildAllowedPaymentMethods(config),
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: orderTotal.toFixed(2),
          currencyCode: DEFAULT_CURRENCY_CODE,
          countryCode: config.countryCode
        },
        merchantInfo: {
          merchantId: config.merchantId,
          merchantName: config.merchantName
        }
      })

      const token = response?.paymentMethodData?.tokenizationData?.token
      const network = response?.paymentMethodData?.info?.cardNetwork
      const displayName = response?.paymentMethodData?.description

      lockCheckout()
      await setGooglePayOnCart({
        variables: {
          cartId,
          token,
          displayName,
          network,
          saveCard: isLoggedIn && saveCard
        }
      })

      unlockCheckout()
      placeOrder()
    } catch (e: any) {
      unlockCheckout()

      // User closing the Google Pay sheet is not a real error - no toast for it.
      if (e?.statusCode === 'CANCELED') {
        return
      }

      params.onError(e instanceof Error ? e : new Error('Google Pay payment failed'))
    }
  }

  return {
    available,
    eligibilityLoading,
    canSaveCard: isLoggedIn,
    saveCard,
    setSaveCard,
    placeGooglePayOrder
  }
}
